/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './FastaysHome.scss';
import AppTextInput from '../../shared/components/app-text-input/AppTextInput';
import AppButton from '../../shared/components/app-button/AppButton';
import { useAppContext } from '../../saas/AppConfigWrapper';
import { getStringFromLS } from '../../shared/helper-utils/localStorage';
import { isNullOrEmpty } from '../../shared/helper-utils/appUtils';
import {
  APP_AGENT_LOGO_URL,
  APP_LOGO_URL,
  APP_USER_AUTH,
} from '../../saas/AppStorageStaticNames';
import { sendEnquiryEmailApi } from '../../api/profileApi';
import AppAlert from '../../shared/components/app-alert/AppAlert';
import AppHeader from '../../shared/components/app-header/AppHeader';
/* ────────────────────────────── DATA ────────────────────────────────── */
const TICKER_ITEMS = [
  'Confidentiality Assured on Every Booking',
  'Same-Day Commission Credit',
  'White-Label Vouchers With Your Agency Logo',
  'Dedicated Relationship Manager',
  'Domestic + International Inventory, One Login',
];

const FEATURES = [
  {
    title: 'Earn on Every Booking',
    desc: 'Transparent, slab-based commission credited straight to your wallet — no chasing, no waiting till month-end.',
  },
  {
    title: 'One Dashboard, Everything',
    desc: 'Flights, hotels, packages, cabs and trains — search, book and manage every PNR from a single screen.',
  },
  {
    title: 'Instant Payouts',
    desc: 'Booking confirmation to your bank account in a couple of clicks. No manual approvals, no follow-up calls.',
  },
  {
    title: 'Support That Answers',
    desc: 'A dedicated relationship manager, plus a 24×7 desk for the moment a booking needs urgent help.',
  },
];

const WHY_ITEMS = [
  {
    title: 'Agent-Only Fares',
    desc: 'Rates the public never sees, on flights and hotels alike.',
  },
  {
    title: 'Same-Day Commission Credit',
    desc: 'Not "end of month" — your wallet updates as bookings confirm.',
  },
  {
    title: 'White-Label Vouchers',
    desc: "Booking confirmations carry your agency's name and logo, not ours.",
  },
  {
    title: 'Zero Hidden Fees',
    desc: 'No surprise charges on cancellations or date changes.',
  },
];

const TESTIMONIALS = [
  {
    quote:
      "The commission used to take days to reflect with our old vendor. With Fastays it's there the same day, and that changes how confidently I quote a customer.",
    name: 'Rakesh Mehta',
    agency: 'Mehta Travels, Lucknow',
    initials: 'RM',
  },
  {
    quote:
      'I run flights, hotels and packages for my clients from one screen now. My team spends less time switching tabs and more time actually selling.',
    name: 'Sunita Iyer',
    agency: 'Wandershine Holidays, Chennai',
    initials: 'SI',
  },
  {
    quote:
      'A booking went wrong at 11pm and someone from Fastays actually answered. That single call is the reason I moved my whole agency over.',
    name: 'Imran Sheikh',
    agency: 'Sheikh Tour & Travels, Indore',
    initials: 'IS',
  },
];

/* ─────────────────── FAQ ACCORDION ITEM ─────────────────── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`fh-faq-item${open ? ' open' : ''}`}>
      <button
        type="button"
        className="fh-faq-q"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>{q}</span>
        <span className="fh-faq-icon">{open ? '−' : '+'}</span>
      </button>
      {open && <div className="fh-faq-a">{a}</div>}
    </div>
  );
}

FaqItem.propTypes = {
  q: PropTypes.string.isRequired,
  a: PropTypes.string.isRequired,
};

/* ─────────────────────────── COMPONENT ──────────────────────────────── */
export default function FastaysHome() {
  const { appConfig } = useAppContext();
  const userAuth = getStringFromLS(APP_USER_AUTH);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [hasNoImage, setHasNoImage] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [submitted, setSubmitted] = useState(false);
  const [alert, setAlert] = useState(undefined);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const apiPayload = {
      name: form.name,
      email: form.email,
      number: form.phone,
      enquirytype: 'Flights',
      querymessage: "I'm Interested, Call Me Back!",
    };
    const response = await sendEnquiryEmailApi(apiPayload);
    if (response && response.ApiStatus && response.ApiStatus.Status === 'OK') {
      setAlert({
        variant: 'success',
        title: '',
        description:
          'Our team has received your query, we will get back to you shortly',
      });
      setSubmitted(true);
      setForm({
        name: '',
        email: '',
        phone: '',
      });
    } else {
      setAlert({
        variant: 'error',
        title: '',
        description:
          'Our team is busy right now, please raise your enquiry later',
      });
    }
  };

  return (
    <div className="fh-root">
      {alert && (
        <AppAlert
          showIcon
          variant={alert.variant}
          title={alert.title}
          description={alert.description}
          onHide={() => setAlert(undefined)}
        />
      )}
      {/* ═══════════════════ HEADER ═══════════════════ */}
      {isNullOrEmpty(userAuth) && (
        <div className="fh-mobile-public-header">
          <AppHeader />
        </div>
      )}
      {isNullOrEmpty(userAuth) && (
        <header className="fh-header">
          <div className="fh-header-logo-container">
            <a href="/" className="fh-header-logo-link">
              {!imageLoadError &&
              !isNullOrEmpty(getStringFromLS(APP_AGENT_LOGO_URL)) ? (
                <img
                  src={getStringFromLS(APP_AGENT_LOGO_URL)}
                  alt=""
                  style={{ maxHeight: `${appConfig?.logoHeight || 44}px` }}
                  onError={(e) => {
                    e.target.src = null;
                    e.currentTarget.onerror = null;
                    setImageLoadError(true);
                  }}
                />
              ) : !hasNoImage &&
                !isNullOrEmpty(getStringFromLS(APP_LOGO_URL)) ? (
                <img
                  src={getStringFromLS(APP_LOGO_URL)}
                  alt=""
                  style={{ maxHeight: `${appConfig?.logoHeight || 44}px` }}
                  onError={(e) => {
                    e.target.src = null;
                    e.currentTarget.onerror = null;
                    setHasNoImage(true);
                  }}
                />
              ) : (
                <span className="fh-logo-fallback">
                  {appConfig?.companyName || 'Fastays'}
                </span>
              )}
            </a>
          </div>
          <nav className="fh-header-links">
            <a href="/about-us">About Us</a>
            <a href="/testimonials">Testimonials</a>
            <a href="/contact-us">Contact Us</a>
          </nav>
          <div className="fh-header-nav">
            <a href="/login" className="fh-btn-primary fh-header-signup-btn">
              Sign Up
            </a>
          </div>
        </header>
      )}

      {/* ═══════════════════ HERO ═══════════════════ */}
      <section
        className={`fh-hero ${
          !isNullOrEmpty(userAuth) ? 'logged-in' : ''
        }`.trim()}
      >
        {/* Left — Copy */}
        <div>
          <div className="fh-tag">
            Fastays Partner Programme · For B2B Travel Agents
          </div>

          <h1 className="fh-hero-title">
            Sell More Trips.
            <br />
            Keep More <span className="fh-accent">Commission.</span>
          </h1>

          <p className="fh-hero-desc">
            One login for flights, hotels, holidays, cabs and trains — with
            agent-only fares, same-day payouts, and a support desk that actually
            picks up.
          </p>

          {isNullOrEmpty(userAuth) && (
            <div className="fh-hero-btns">
              <a href="/agent-registration" className="fh-btn-primary">
                Become a Partner
                <span className="fh-btn-arrow">→</span>
              </a>
              <a href="/login" className="fh-btn-outline">
                Agent Login
              </a>
            </div>
          )}

          <div className="fh-stats">
            {[
              { value: '600+', label: 'Active Agents Nationwide' },
              { value: '190+', label: 'Countries Covered' },
              { value: '100K+', label: 'Bookings Processed' },
              { value: '4.8 ★', label: 'Agent Satisfaction Score' },
            ].map((s) => (
              <div key={s.label}>
                <div className="fh-stat-value">{s.value}</div>
                <div className="fh-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — Call Me Back form */}
        {isNullOrEmpty(userAuth) && (
          <div className="fh-callback-card">
            <div className="fh-callback-title">
              I&apos;m Interested, Call Me Back!
            </div>
            <div className="fh-callback-subtitle">
              Leave your details and a partner executive will call you back.
            </div>

            <form onSubmit={handleSubmit}>
              <AppTextInput
                name="name"
                placeholderText="Full Name"
                value={form.name}
                onChange={handleChange('name')}
                wrapperClassName="fh-field"
              />
              <AppTextInput
                name="email"
                type="email"
                placeholderText="Email Address"
                value={form.email}
                onChange={handleChange('email')}
                wrapperClassName="fh-field"
              />
              <AppTextInput
                name="phone"
                type="tel"
                placeholderText="Contact Number / Mobile"
                value={form.phone}
                onChange={handleChange('phone')}
                wrapperClassName="fh-field"
              />
              <AppButton
                btnText={submitted ? '✓ Request Sent!' : 'Submit'}
                btnType="submit"
                className="fh-submit-btn w-100"
              />
            </form>

            <div className="fh-callback-note">
              Our support executives will help you activate your account
              <br />
              and guide you along the way.
            </div>
          </div>
        )}
      </section>

      {/* ═══════════════════ TICKER ═══════════════════ */}
      <div className="fh-ticker" aria-hidden="true">
        <div className="fh-ticker-track">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
            <span
              className="fh-ticker-item"
              key={`${item}-${i < TICKER_ITEMS.length ? 'a' : 'b'}`}
            >
              <span className="fh-ticker-check">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path
                    d="M2 5l2.5 2.5L8 3"
                    stroke="#e8962e"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* ═══════════════════ FEATURES ═══════════════════ */}
      <section className="fh-section">
        <div className="fh-section-tag">Built for Agents</div>
        <h2 className="fh-section-title">
          Designed Around How Your Day Actually Runs
        </h2>
        <p className="fh-section-desc">
          Not a generic booking widget — a working desk for an agency that
          books, tracks and gets paid every single day.
        </p>

        <div className="fh-features-grid">
          {FEATURES.map((f) => (
            <div className="fh-feature-card" key={f.title}>
              <div className="fh-feature-icon">
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <path
                    d="M4.5 11.5l4.5 4.5 8.5-9"
                    stroke="#e8962e"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="fh-feature-title">{f.title}</div>
              <div className="fh-feature-desc">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════════ WHY FASTAYS ═══════════════════ */}
      <section className="fh-section-alt">
        <div className="fh-section-tag">Why Agents Choose Fastays</div>
        <h2 className="fh-section-title">
          The Details That Actually Move Your Margins
        </h2>
        <p className="fh-section-desc">
          Every feature is built with one question in mind: does this help the
          agent earn more, faster?
        </p>

        <div className="fh-why-grid">
          {WHY_ITEMS.map((w) => (
            <div className="fh-why-card" key={w.title}>
              <div className="fh-why-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path
                    d="M3.5 9.5l4 4L14.5 5"
                    stroke="#2e9e6b"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="fh-why-title">{w.title}</div>
              <div className="fh-why-desc">{w.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════════ TESTIMONIALS ═══════════════════ */}
      <section className="fh-testimonials">
        <div className="fh-section-tag">Partner Stories</div>
        <h2 className="fh-section-title">
          What Agents Say After Switching to Fastays?{' '}
        </h2>

        <div className="fh-testi-carousel">
          <div className="fh-testi-track">
            {TESTIMONIALS.map((t) => (
              <div className="fh-testi-card" key={t.name}>
                <div className="fh-testi-stars">
                  {'★★★★★'.split('').map((star) => (
                    <span key={star}>{star}</span>
                  ))}
                </div>
                <p className="fh-testi-quote">&ldquo;{t.quote}&rdquo;</p>
                <div className="fh-testi-author">
                  <div className="fh-testi-avatar">{t.initials}</div>
                  <div>
                    <div className="fh-testi-name">{t.name}</div>
                    <div className="fh-testi-agency">{t.agency}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ HOW & WHY ═══════════════════ */}
      <section className="fh-how-why">
        <div className="fh-section-tag">Before You Sign Up</div>
        <h2 className="fh-section-title">
          How &amp; Why to Register With Fastays?
        </h2>
        <p className="fh-section-desc">
          The short version of the process, and the short version of what&apos;s
          in it for you.
        </p>

        <div className="fh-hw-grid">
          {/* LEFT — How to Register */}
          <div className="fh-hw-card">
            <div className="fh-hw-preview fh-hw-preview--steps">
              {[
                'Share your agency details',
                'Upload PAN & KYC',
                'Activate your wallet',
              ].map((s) => (
                <div className="fh-hw-step-row" key={s}>
                  <span className="fh-hw-step-check">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path
                        d="M2 5l2.5 2.5L8 3"
                        stroke="#2e9e6b"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span>{s}</span>
                </div>
              ))}
            </div>

            <div className="fh-hw-body">
              <div className="fh-hw-sub-title">How to Register</div>
              {[
                {
                  label: 'Share Your Details',
                  desc: 'name, mobile number and city. Takes under two minutes.',
                },
                {
                  label: 'Upload PAN & KYC',
                  desc: 'most agents are approved within a working day.',
                },
                {
                  label: 'Activate Your Wallet',
                  desc: "set up your wallet or credit line and you're ready to book.",
                },
              ].map((item) => (
                <div className="fh-hw-point" key={item.label}>
                  <span className="fh-hw-point-check">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path
                        d="M2 5l2.5 2.5L8 3"
                        stroke="#e8962e"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span>
                    <strong>{item.label}</strong> — {item.desc}
                  </span>
                </div>
              ))}
              <a href="/agent-registration" className="fh-hw-link">
                Start Your Registration →
              </a>
            </div>
          </div>

          {/* RIGHT — Why Register */}
          <div className="fh-hw-card">
            <div className="fh-hw-preview fh-hw-preview--chart">
              <div className="fh-chart-bars">
                {[30, 45, 55, 65, 80, 100].map((h) => (
                  <div
                    key={h}
                    className="fh-chart-bar"
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
              <div className="fh-chart-label">Your Monthly Commission</div>
            </div>

            <div className="fh-hw-body">
              <div className="fh-hw-sub-title">Why Register</div>
              {[
                {
                  label: 'Higher Margins',
                  desc: 'on every booking with agent-only fares.',
                },
                {
                  label: 'Same-Day Payouts',
                  desc: 'commission lands in your wallet, not at month-end.',
                },
                {
                  label: 'A Team That Picks Up',
                  desc: 'dedicated RM plus 24×7 support.',
                },
                {
                  label: 'No Cost to Join',
                  desc: 'free registration, no hidden charges.',
                },
              ].map((item) => (
                <div className="fh-hw-point" key={item.label}>
                  <span className="fh-hw-point-check">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path
                        d="M2 5l2.5 2.5L8 3"
                        stroke="#e8962e"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span>
                    <strong>{item.label}</strong> — {item.desc}
                  </span>
                </div>
              ))}
              <a href="/agent-registration" className="fh-hw-link">
                See What You&apos;ll Earn →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ FAQ ═══════════════════ */}
      <section className="fh-faq">
        <div className="fh-section-tag">Questions, Answered</div>
        <h2 className="fh-section-title">Frequently Asked by New Partners</h2>

        <div className="fh-faq-list">
          {[
            {
              q: 'What Documents Do I Need to Register?',
              a: 'A PAN card and one basic KYC document — a business registration certificate, electricity bill, or bank statement — are enough to get started.',
            },
            {
              q: 'How Soon Do I Get Paid My Commission?',
              a: ' Your commission is credited after the booking is successfully completed, according to our regular payout cycle',
            },
            {
              q: 'Is There a Fee to Join the Partner Programme?',
              a: 'No. The Fastays Partner Programme is completely free to join with no registration fees.',
            },
            {
              q: 'Can I Keep Using My Existing Booking Tools Alongside Fastays?',
              a: 'Yes. You can continue using your existing booking tools while also booking through Fastays.',
            },
            {
              q: 'Who Do I Contact if a Booking Needs Urgent Help?',
              a: 'Our dedicated support team is available to assist you with any urgent booking issues or travel queries.',
            },
          ].map((item) => (
            <FaqItem key={item.q} q={item.q} a={item.a} />
          ))}
        </div>
      </section>

      {/* ═══════════════════ CTA BANNER ═══════════════════ */}
      {isNullOrEmpty(userAuth) && (
        <section className="fh-cta-banner">
          <div className="fh-cta-blob" />
          <div className="fh-cta-inner">
            <div className="fh-cta-tag">Ready When You Are</div>
            <h2 className="fh-cta-title">
              Your Customers Are Already Searching.
              <br />
              Start Earning Before They Book Elsewhere.
            </h2>
            <p className="fh-cta-desc">
              Join in minutes and have your wallet active before the week is
              out.
            </p>
            <div className="fh-cta-btns">
              <a href="/agent-registration" className="fh-btn-primary">
                Become a Partner
                <span className="fh-btn-arrow">→</span>
              </a>
              <a href="/contact-us" className="fh-cta-btn-outline">
                Talk to Our Team
              </a>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
